import {
  Grid,
  Input,
  InputVariant,
  Paper,
  SnackbarVariant,
  Text,
  TextSize,
  TextWeight,
  useSnackbar
} from '@focaldata/cin-ui-components'
import React, { useEffect, useState } from 'react'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import { getQuestionEntryPrefix } from '../../../utils/questionnaireUtils'
import { MarketVisibilityFilter } from '../../Questionnaire/MarketVisibility/marketVisibility'
import { useSaveTranslation } from '../hooks/useSaveTranslation'
import { dispatchSurveyFieldSave } from '../lib/dispatchFieldSave'
import {
  TranslatableField,
  extractTranslatableFields
} from '../lib/extractTranslatableFields'

const upperLanguageName = (code: string): string =>
  getLanguageLabel(code).toUpperCase()

interface FieldRowProps {
  field: TranslatableField
  targetLanguage: string
  onSave: (text: string) => void
  isFirst: boolean
  visibleLabel?: string
  indent?: number
}

const FieldRow: React.FC<FieldRowProps> = ({
  field,
  targetLanguage,
  onSave,
  isFirst,
  visibleLabel,
  indent = 0
}) => {
  const initial = field.getCurrent(targetLanguage)
  const [draft, setDraft] = useState<string>(initial)

  useEffect(() => {
    setDraft(initial)
  }, [initial, targetLanguage])

  const persist = () => {
    if (draft === initial) return
    onSave(draft)
  }

  // Render with or without visible label depending on whether one was provided
  const hasVisibleLabel = visibleLabel !== undefined

  return (
    <div style={{ marginTop: isFirst ? 0 : 12, marginLeft: indent }}>
      {hasVisibleLabel && (
        <div style={{ marginBottom: 4 }}>
          <Text size={TextSize.s} weight={TextWeight.SemiBold}>
            {visibleLabel}
          </Text>
        </div>
      )}
      <Grid container direction="row" spacing={2} alignItems="flex-start">
        <Grid item xs={6}>
          {/* ms (14px) matches the input opposite it; weight alone marks out
              the question text from its options. */}
          <Text
            size={TextSize.ms}
            weight={isFirst ? TextWeight.SemiBold : TextWeight.Regular}
          >
            {field.master || ' '}
          </Text>
        </Grid>
        <Grid item xs={6}>
          {/* `multiline` alone auto-grows; adding `rows` pins the height and
              clips long question text to one line. */}
          <Input
            multiline
            fullWidth
            variant={InputVariant.Outlined}
            ariaLabel={`${field.label} in ${targetLanguage}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={persist}
          />
        </Grid>
      </Grid>
    </div>
  )
}

interface FieldGroup {
  key: string
  heading?: string
  fields: Array<{
    field: TranslatableField
    visibleLabel?: string
    indent?: number
  }>
}

/**
 * Groups fields by family for hierarchical display.
 *
 * - Highlighter: Question / Content / Categories groups
 * - MaxDiff: Most / Least / Attribute labels
 * - Scale: by position
 * - Conjoint: attribute headings with nested options
 * - All others: flat, no grouping
 */
const groupFields = (fields: TranslatableField[]): FieldGroup[] => {
  const groups: FieldGroup[] = []

  const add = (
    key: string,
    heading: string | undefined,
    field: TranslatableField,
    visibleLabel?: string,
    indent?: number
  ) => {
    const existing = groups.find((group) => group.key === key)
    const row = { field, visibleLabel, indent }
    if (existing) existing.fields.push(row)
    else groups.push({ key, heading, fields: [row] })
  }

  fields.forEach((field) => {
    switch (field.kind) {
      // ─── Highlighter ───
      case 'highlighterQuestionText':
        add('highlighter-question', 'Question', field)
        return
      case 'highlighterContent':
        add('highlighter-content', 'Content', field)
        return
      case 'highlighterCategory':
        add('highlighter-categories', 'Categories', field, field.label)
        return

      // ─── MaxDiff ───
      case 'maxDiffMostLabel':
        add('maxdiff-labels', 'MaxDiff labels', field, 'Most')
        return
      case 'maxDiffLeastLabel':
        add('maxdiff-labels', undefined, field, 'Least')
        return
      case 'maxDiffAttributeLabel':
        add('maxdiff-labels', undefined, field, 'Attribute')
        return

      // ─── Scale ───
      case 'scaleLabel':
        add(
          'scale-labels',
          'Scale labels',
          field,
          field.label // e.g. "Left label", "Central label", "Right label"
        )
        return

      // ─── Conjoint ───
      case 'conjointText':
        add('conjoint-question', 'Question', field)
        return
      case 'conjointAttributeName': {
        // Extract attribute number from label like "Attribute 1"
        const attrId = field.conjoint?.attributeId ?? 'unknown'
        add(`conjoint-attribute-${attrId}`, field.label, field, 'Name')
        return
      }
      case 'conjointAttributeOption': {
        // Nest under its attribute heading
        const attrId = field.conjoint?.attributeId ?? 'unknown'
        // Extract level number from label like "Attribute 1 — level 2"
        const levelMatch = field.label.match(/level (\d+)/)
        const levelLabel = levelMatch ? `Level ${levelMatch[1]}` : field.label
        add(`conjoint-attribute-${attrId}`, undefined, field, levelLabel, 16)
        return
      }

      // ─── All others: flat display, keep master's visual treatment ───
      default:
        add(`field-${field.key}`, undefined, field)
    }
  })

  return groups
}

interface Props {
  entry: DraftQuestionnaireEntry
  targetLanguage: string
  primaryLanguage: string
  // Hides options this language's markets don't show, so the card lists exactly
  // what completion counts.
  isVisible?: MarketVisibilityFilter
}

const EntryTranslationCard: React.FC<Props> = ({
  entry,
  targetLanguage,
  primaryLanguage,
  isVisible
}) => {
  const fields = extractTranslatableFields(entry, isVisible)
  const fieldGroups = groupFields(fields)
  // Same per-type label (T1/Q1/…) the survey editor card and docx export show.
  const positionLabel = getQuestionEntryPrefix(entry)
  const save = useSaveTranslation()
  const { enqueueSnackbar } = useSnackbar()

  if (fields.length === 0) {
    return null
  }

  // Interactive edits save one field at a time via the per-field mutations.
  // The bulk importQuestionnaireV2 path (used by auto-translate and the docx
  // importer) is deliberately NOT used here: sending the whole entry on every
  // blur would read sibling fields from the Apollo cache, so a second blur
  // firing before the first import's refetch landed could overwrite a
  // just-typed value with stale data, and it would trigger a full
  // questionnaire refetch per keystroke-blur. Import only pays off when many
  // fields are written in a single call.
  //
  // The save dispatch can throw synchronously — wrap to prevent unhandled
  // exception escaping the blur handler. Note the backend semantic: for scale
  // labels a blank value IS the delete mechanism, so clearing from the editor
  // is legitimate; only auto-translate guards against sending blanks.
  const handleSave = (field: TranslatableField, text: string) => {
    try {
      dispatchSurveyFieldSave(save, field, text, targetLanguage)
    } catch (error) {
      enqueueSnackbar(
        `Failed to save "${field.label}" — ${
          error instanceof Error ? error.message : 'unexpected error'
        }.`,
        { variant: SnackbarVariant.Error }
      )
    }
  }

  return (
    <Paper>
      <div style={{ padding: 16 }}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={6}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              {`${upperLanguageName(primaryLanguage)} (${positionLabel})`}
            </Text>
          </Grid>
          <Grid item xs={6}>
            <Text size={TextSize.s} weight={TextWeight.SemiBold}>
              {upperLanguageName(targetLanguage)}
            </Text>
          </Grid>
        </Grid>
        <div style={{ marginTop: 8 }}>
          {fieldGroups.map((group, groupIndex) => (
            <div
              key={group.key}
              style={{ marginTop: groupIndex === 0 ? 0 : 20 }}
            >
              {group.heading && (
                <div style={{ marginBottom: 8 }}>
                  <Text size={TextSize.m} weight={TextWeight.SemiBold}>
                    {group.heading}
                  </Text>
                </div>
              )}
              {group.fields.map(
                ({ field, visibleLabel, indent }, fieldIndex) => (
                  <FieldRow
                    key={field.key}
                    field={field}
                    targetLanguage={targetLanguage}
                    onSave={(text) => handleSave(field, text)}
                    isFirst={groupIndex === 0 && fieldIndex === 0}
                    visibleLabel={visibleLabel}
                    indent={indent}
                  />
                )
              )}
            </div>
          ))}
        </div>
      </div>
    </Paper>
  )
}

export default EntryTranslationCard
